import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import {
    isEInvoiceSingleUseToken,
    SingleUseTokensTable,
} from '@/components/user/SingleUseTokensCard';
import { paymentsApi, userApi } from '@/lib/pillarApi';
import type { ListSingleUseTokensResponse, SingleUseTokenListItem } from '@pillar-portal/api-types';

export type SingleUseTokensPageVariant = 'user-management' | 'e-invoice';

interface SingleUseTokensListPageProps {
    variant: SingleUseTokensPageVariant;
    pageTitle: string;
}

export function SingleUseTokensListPage(props: SingleUseTokensListPageProps): React.ReactElement {
    const queryClient = useQueryClient();
    const [showAllHistoricalTokens, setShowAllHistoricalTokens] = useState(false);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);
    const [operationErrorMessage, setOperationErrorMessage] = useState<string | null>(null);
    const outstandingOnly: boolean = !showAllHistoricalTokens;
    const queryKeyPrefix = useMemo(
        () =>
            [
                'singleUseTokensPage',
                props.variant,
                `outstandingOnly=${String(outstandingOnly)}`,
            ] as const,
        [outstandingOnly, props.variant]
    );

    const expireMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await userApi.expireSingleUseTokenAdmin(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: ['singleUseTokensPage'] });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            setOperationErrorMessage(
                error instanceof Error ? error.message : 'Failed to expire token'
            );
        },
    });

    const resendMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await userApi.resendSingleUseTokenAdmin(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: ['singleUseTokensPage'] });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            setOperationErrorMessage(
                error instanceof Error ? error.message : 'Failed to resend token'
            );
        },
    });

    const revokePaymentLinkMutation = useMutation({
        mutationFn: async (params: {
            paymentRequestUuid: string;
            merchantOrganizationUuid: string;
        }) => {
            return await paymentsApi.revokePaymentRequestLink(params.paymentRequestUuid, {
                merchant_organization_uuid: params.merchantOrganizationUuid,
            });
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: ['singleUseTokensPage'] });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            setOperationErrorMessage(
                error instanceof Error ? error.message : 'Failed to revoke payment link'
            );
        },
    });

    return (
        <InfiniteScrollList<SingleUseTokenListItem, string, typeof queryKeyPrefix>
            pageTitle={props.pageTitle}
            queryPlaceholder="Search by user email, token email, token type, or UUID"
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: ListSingleUseTokensResponse =
                    await userApi.listSingleUseTokensAdmin({
                        outstanding_only: outstandingOnly,
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                return { items: response.tokens, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().uuid().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected UUID cursor');
                }
                return parseResult.data;
            }}
            controls={
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="showAllSingleUseTokens"
                        checked={showAllHistoricalTokens}
                        onCheckedChange={(checked) => setShowAllHistoricalTokens(checked === true)}
                    />
                    <Label htmlFor="showAllSingleUseTokens">Show all historical tokens</Label>
                </div>
            }
            stateCopy={{
                loadingLabel: 'Loading single use tokens...',
                emptyLabel: outstandingOnly
                    ? `No outstanding ${props.pageTitle.toLowerCase()}.`
                    : `No historical ${props.pageTitle.toLowerCase()}.`,
                endOfListLabel: `End of ${props.pageTitle.toLowerCase()}.`,
                genericErrorLabel: 'Failed to load single use tokens.',
            }}
            renderItems={({ items }) => {
                const filteredTokens = items.filter((token) =>
                    props.variant === 'e-invoice'
                        ? isEInvoiceSingleUseToken(token)
                        : !isEInvoiceSingleUseToken(token)
                );
                const isBusy =
                    expireMutation.isPending ||
                    resendMutation.isPending ||
                    revokePaymentLinkMutation.isPending;

                return (
                    <div className="space-y-4">
                        {operationMessage !== null ? (
                            <Alert>
                                <AlertDescription>{operationMessage}</AlertDescription>
                            </Alert>
                        ) : null}
                        {operationErrorMessage !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>{operationErrorMessage}</AlertDescription>
                            </Alert>
                        ) : null}
                        {filteredTokens.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                {props.variant === 'e-invoice'
                                    ? 'No E-Invoice tokens match the current filter.'
                                    : 'No user management tokens match the current filter.'}
                            </p>
                        ) : (
                            <SingleUseTokensTable
                                tokens={filteredTokens}
                                showUserColumns={props.variant === 'user-management'}
                                isBusy={isBusy}
                                variant={props.variant}
                                onExpire={(singleUseAuthTokenId) => {
                                    expireMutation.mutate(singleUseAuthTokenId);
                                }}
                                onRevokePaymentRequest={(
                                    paymentRequestUuid,
                                    merchantOrganizationUuid
                                ) => {
                                    revokePaymentLinkMutation.mutate({
                                        paymentRequestUuid,
                                        merchantOrganizationUuid,
                                    });
                                }}
                                onResend={(singleUseAuthTokenId) => {
                                    resendMutation.mutate(singleUseAuthTokenId);
                                }}
                            />
                        )}
                    </div>
                );
            }}
        />
    );
}
